import { computed } from 'vue'

/**
 * Shared composable for pricing calculations
 * Eliminates duplicated pricing logic between components
 */
export function usePricing(productTierPricing: Record<string, Record<string, number>>) {

  /**
   * Get tier 3 price for a product from the productTierPricing data
   */
  const getTier3Price = (sku: string): number | undefined => {
    if (productTierPricing && productTierPricing[sku]) {
      return productTierPricing[sku].tier3
    }
    return undefined
  }

  /**
   * Get tier 2 price for a product
   */
  const getTier2Price = (sku: string): number | undefined => {
    if (productTierPricing && productTierPricing[sku]) {
      return productTierPricing[sku].tier2
    }
    return undefined
  }

  /**
   * Get tier 1 price for a product
   */
  const getTier1Price = (sku: string): number | undefined => {
    if (productTierPricing && productTierPricing[sku]) {
      return productTierPricing[sku].tier1
    }
    return undefined
  }

  /**
   * Get all tier prices for a product
   */
  const getAllTierPrices = (sku: string) => {
    return {
      tier1: getTier1Price(sku),
      tier2: getTier2Price(sku),
      tier3: getTier3Price(sku),
    }
  }

  /**
   * Check if product has pricing data
   */
  const hasValidPricing = (sku: string): boolean => {
    return !!productTierPricing?.[sku]
  }

  /**
   * Get best available price for a product (usually tier 3)
   */
  const getBestPrice = (sku: string): number | undefined => {
    const pricing = productTierPricing?.[sku]
    if (!pricing) return undefined

    // Return the lowest available tier price
    return pricing.tier3 ?? pricing.tier2 ?? pricing.tier1
  }

  return {
    getTier1Price,
    getTier2Price,
    getTier3Price,
    getAllTierPrices,
    hasValidPricing,
    getBestPrice,
  }
}

/**
 * Utility functions for price formatting (can be shared)
 */
export function usePriceFormatting(currency: string) {

  const formatPrice = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '-'

    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const formatNumber = (value: number | undefined): string => {
    if (value === undefined || value === null) return '-'

    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  return {
    formatPrice,
    formatNumber,
  }
}